import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { yearOneTargets, type ImpactSnapshot } from '@juvo/schemas';
import { formatJvc } from '@juvo/utils';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

@customElement('jv-impact-page')
export class JvImpactPage extends LitElement {
  static styles = css`
    h1 {
      font-size: var(--jv-fs-display);
      margin: 0 0 0.7rem;
    }
    .intro {
      color: var(--jv-color-muted);
      max-width: 38rem;
      margin-bottom: 2rem;
    }
    .grid {
      display: grid;
      gap: 1rem;
    }
    @media (min-width: 800px) {
      .grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    .stat {
      font-family: var(--jv-font-display);
      font-size: 2.1rem;
      margin: 0;
    }
    .label {
      color: var(--jv-color-muted);
    }
    h2 {
      font-size: var(--jv-fs-xl);
    }
  `;

  @state() private snapshot: ImpactSnapshot | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    store.client.getImpact().then((snapshot) => {
      this.snapshot = snapshot;
    });
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  render() {
    const t = store.t.bind(store);
    const live = this.snapshot;
    return html`
      <jv-page>
        <h1>${t('impact.title')}</h1>
        <p class="intro">${t('impact.intro')}</p>
        <h2>${t('impact.live')}</h2>
        <div class="grid">
          ${this.stat(live?.kgRefurbished ?? 0, t('impact.kg'))}
          ${this.stat(live?.cohortsRun ?? 0, t('impact.cohorts'))}
          ${this.stat(live?.partnersActive ?? 0, t('impact.partners'))}
          ${this.stat(live?.deployments ?? 0, t('impact.deployments'))}
          ${this.stat(formatJvc(live?.jvcIssued ?? 0), t('impact.jvc'))}
        </div>
        <h2>${t('impact.target')}</h2>
        <div class="grid">
          ${this.stat(yearOneTargets.kgRefurbished, t('impact.kg'))}
          ${this.stat(yearOneTargets.cohortsRun, t('impact.cohorts'))}
          ${this.stat(yearOneTargets.partnersActive, t('impact.partners'))}
          ${this.stat(yearOneTargets.deployments, t('impact.deployments'))}
        </div>
      </jv-page>
    `;
  }

  private stat(value: string | number, label: string) {
    return html`<jv-card>
      <p class="stat">${value}</p>
      <p class="label">${label}</p>
    </jv-card>`;
  }
}

@customElement('jv-geeks-page')
export class JvGeeksPage extends LitElement {
  static styles = css`
    h1 {
      font-size: var(--jv-fs-display);
    }
    p {
      max-width: 38rem;
      color: var(--jv-color-muted);
    }
    .script {
      font-size: 0.95rem;
      color: var(--jv-color-amber);
      margin-bottom: 0.5rem;
    }
    .script::before {
      content: '// ';
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <div class="script">${t('home.ctaGeekTag')}</div>
        <h1>${t('geeks.title')}</h1>
        <p>${t('geeks.body')}</p>
        <p>
          <jv-button href="/signup">${t('geeks.cta')}</jv-button>
        </p>
      </jv-page>
    `;
  }
}

@customElement('jv-not-found-page')
export class JvNotFoundPage extends LitElement {
  render() {
    return html`<jv-page
      ><jv-empty-state heading=${store.t('notfound')}></jv-empty-state
    ></jv-page>`;
  }
}
